import { type fabric } from 'fabric';
import ShapeBase from './ShapeBase';
import tools from '../tools';
import ProgressBarControl from '../controls/ProgressBarControl';
import Color from 'color';
import { type TextProps } from '../objects/EmbeddedTextBehaviorsV2';
import GenericListTool from '../tools/GenericListTool';
import { svgToIcon } from '../utils/svg';
import shapeProgressBar from '../assets/icons/shapeProgressBar.svg';
import progressBarRoundedSVG from '../assets/icons/progressBarRounded.svg';
import progressBarPointRightSVG from '../assets/icons/progressBarPointRight.svg';
import progressBarPointLeftSVG from '../assets/icons/progressBarPointLeft.svg';
import progressBarPointBothSVG from '../assets/icons/progressBarPointBoth.svg';
import progressBarPointRightBothSVG from '../assets/icons/progressBarPointRightBoth.svg';
import { camelToSentenceCase } from '../utils/string';
import type Shape from '../objects/Shape';
import { ToolTextLocation } from '../tools/ToolTextLocation';
import {
  hasIntegrationRecordReference,
  hasRecordReference,
} from '../objects/guards';

class ShapeProgressBar extends ShapeBase {
  progress: number;
  textAlign: string;

  textProps: TextProps = {
    allowedStyles: 'any',
    location: 'user',
    textAlign: 'shape',
    verticalAlign: 'center',
    fontSize: 'user',
    overflow: 'visible',
    wrapText: 'growText',
    margins: 'noScale',
  };

  static VARIATION_ROUNDED = 'rounded';
  static VARIATION_POINT_RIGHT = 'pointRight';
  static VARIATION_POINT_BOTH = 'pointBoth';
  static VARIATION_RIGHT_BOTH = 'pointRightBoth';
  static VARIATION_POINT_LEFT = 'pointLeft';

  static variationChoices = [
    {
      value: ShapeProgressBar.VARIATION_ROUNDED,
      iconSVG: progressBarRoundedSVG,
    },
    {
      value: ShapeProgressBar.VARIATION_POINT_RIGHT,
      iconSVG: progressBarPointRightSVG,
    },
    {
      value: ShapeProgressBar.VARIATION_RIGHT_BOTH,
      iconSVG: progressBarPointRightBothSVG,
    },
    {
      value: ShapeProgressBar.VARIATION_POINT_BOTH,
      iconSVG: progressBarPointBothSVG,
    },
    {
      value: ShapeProgressBar.VARIATION_POINT_LEFT,
      iconSVG: progressBarPointLeftSVG,
    },
  ].map(choice => {
    return {
      ...choice,
      toolName: camelToSentenceCase(choice.value),
    };
  });

  static toolVariation = GenericListTool({
    toolName: 'Variation',
    attribute: 'variation',
    iconSVG: progressBarRoundedSVG,
    choices: this.variationChoices,
  });

  static renderIcon() {
    return svgToIcon(shapeProgressBar);
  }

  static defaultWidth() {
    return 200;
  }

  static defaultHeight() {
    return 20;
  }

  static shapeControls: fabric.Controls = {
    ...this.createDefaultControls({ noRotate: true }),
    progress: new ProgressBarControl({}),
  };

  constructor(fabricObject: Shape) {
    super(fabricObject);

    this.textAlign = 'right'; // TODO: make this automatic.
    this.textLocation ||= 'above';

    const defaults = this.fabricObject.controller.defaultsManager.for(
      'shape',
      'progressBar'
    );
    this.progress ??= defaults.progress;
  }

  tools() {
    const progressBarTools = [
      [ShapeProgressBar.toolVariation],
      [tools.ToolFillColor],
    ];

    const hasRecord =
      hasRecordReference(this.fabricObject) ||
      hasIntegrationRecordReference(this.fabricObject);
    if (hasRecord) {
      progressBarTools.push([ToolTextLocation]);
    }

    return progressBarTools;
  }

  textTools() {
    return [
      [
        ToolTextLocation,
        tools.ToolTextColor,
        tools.ToolTextFormat,
        tools.ToolFontSize,
      ],
    ];
  }

  cornerRadius() {
    return (
      Math.min(this.width * this.scaleX, (this.height / 2) * this.scaleY) * 0.15
    );
  }

  shapeTextAlign(): 'left' | 'right' | 'center' {
    switch (this.textLocation) {
      case 'left':
        return 'right';
      case 'below':
      case 'above':
      case 'right':
      case 'inside':
      default:
        return 'left';
    }
  }

  calcTextMargin() {
    const defaultMargin = {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    };

    switch (this.textLocation) {
      case 'left':
        return { ...defaultMargin, right: 5 };
      case 'right':
        return { ...defaultMargin, left: 5 };
      case 'below':
        return { ...defaultMargin, top: 5 };
      case 'inside':
        return { ...defaultMargin, left: 5, right: 5 };
      case 'above':
      default:
        return { ...defaultMargin, bottom: 5 };
    }
  }

  render(ctx: CanvasRenderingContext2D) {
    const w = this.width;
    const h = this.height;
    const top = -h / 2;
    const r = this.cornerRadius();
    const rx = r / this.scaleX;
    const ry = r / this.scaleY;

    const lighterFillColor = Color(this.fill ?? undefined)
      .mix(Color('white'), 0.5)
      .hex();

    const drawOutline = () => {
      const pointOffset = Math.min((h * this.scaleY) / 3, 20) / this.scaleX;

      ctx.beginPath();
      switch (this.variation || ShapeProgressBar.VARIATION_ROUNDED) {
        case ShapeProgressBar.VARIATION_ROUNDED:
          ctx.moveTo(-w / 2 + rx, top);
          ctx.lineTo(w / 2 - rx, top);
          ctx.ellipse(w / 2 - rx, top + ry, rx, ry, 0, -Math.PI / 2, 0);
          ctx.lineTo(w / 2, h / 2 - ry);
          ctx.ellipse(w / 2 - rx, h / 2 - ry, rx, ry, 0, 0, Math.PI / 2);
          ctx.lineTo(-w / 2 + rx, h / 2);
          ctx.ellipse(-w / 2 + rx, h / 2 - ry, rx, ry, 0, Math.PI / 2, Math.PI);
          ctx.lineTo(-w / 2, h / 2 - ry);
          ctx.ellipse(-w / 2 + rx, top + ry, rx, ry, 0, Math.PI, 1.5 * Math.PI);
          break;

        case ShapeProgressBar.VARIATION_POINT_RIGHT:
          ctx.moveTo(-w / 2, top);
          ctx.lineTo(w / 2 - pointOffset, top);
          ctx.lineTo(w / 2, 0);
          ctx.lineTo(w / 2 - pointOffset, h / 2);
          ctx.lineTo(-w / 2, h / 2);
          ctx.lineTo(-w / 2, top);
          break;

        case ShapeProgressBar.VARIATION_POINT_LEFT:
          ctx.moveTo(w / 2, top);
          ctx.lineTo(-w / 2 + pointOffset, top);
          ctx.lineTo(-w / 2, 0);
          ctx.lineTo(-w / 2 + pointOffset, h / 2);
          ctx.lineTo(w / 2, h / 2);
          ctx.lineTo(w / 2, top);
          break;

        case ShapeProgressBar.VARIATION_RIGHT_BOTH:
          ctx.moveTo(-w / 2, top);
          ctx.lineTo(w / 2 - pointOffset, top);
          ctx.lineTo(w / 2, 0);
          ctx.lineTo(w / 2 - pointOffset, h / 2);
          ctx.lineTo(-w / 2, h / 2);
          ctx.lineTo(-w / 2 + pointOffset, 0);
          ctx.lineTo(-w / 2, top);
          break;

        case ShapeProgressBar.VARIATION_POINT_BOTH:
          ctx.moveTo(-w / 2 + pointOffset, top);
          ctx.lineTo(w / 2 - pointOffset, top);
          ctx.lineTo(w / 2, 0);
          ctx.lineTo(w / 2 - pointOffset, h / 2);
          ctx.lineTo(-w / 2 + pointOffset, h / 2);
          ctx.lineTo(-w / 2, 0);
          ctx.lineTo(-w / 2 + pointOffset, top);
          break;
      }
      ctx.closePath();
    };

    if (this.canvas.getZoom() > 0.5) {
      // Draw the outline shadow
      ctx.save();
      drawOutline();
      ctx.shadowBlur = 8;
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      this._renderStroke(ctx);
      ctx.restore();
    }

    // Draw the fill, clipped by  the outline.
    ctx.save();
    drawOutline();
    ctx.clip();
    ctx.fillStyle = 'white';
    ctx.fillRect(-w / 2, top, w, h);
    ctx.beginPath();
    ctx.rect(-w / 2, top, w * this.progress, h);
    ctx.fillStyle = lighterFillColor;
    ctx.fill();
    ctx.restore();

    // Draw the outline.
    ctx.save();
    drawOutline();
    this.stroke = this.fill;
    this._renderStroke(ctx);
    ctx.restore();

    this.renderFieldContent(ctx);
  }
}

export default ShapeProgressBar;
